import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, MenuItem, Menu, Chip, Button } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { getWorkspacePath } from '../../utils/workspaceUtils';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalBody, ModalHeader } from 'reactstrap';

import './index.css'
import TableView from './TableView';
import QuickView from './QuickView';
import PipelineView from './PipelineView';
import PipelineSettings from './PipelineSettings';
import { getProperties } from '../../store/Objects';
import ViewManager from '../Objects/ViewManager.js';
import TitleRoute from '../../components/titleRoute.js';
import { bulkDeleteObjects } from '../../store/Objects';
import ImportWizard from '../../components/ImportWizard';
import ExportWizard from '../../components/ExportWizard';
import { buildMongoQuery } from '../../utils/filterConfig';
import TagsManager from '../../components/Tags/TagsManager';
import BulkActionsBar from '../../components/BulkActionsBar';
import Activities from '../../components/Activities/index.js';
import { setActiveActivityBox } from '../../store/Activities';
import QuickFilters from '../../components/Filters/QuickFilters.js';
import { dealsQuickFilterConfig } from '../../utils/quickFiltersConfig.js';
import AdvancedFilterModal from '../../components/Filters/AdvancedFilter.js';
import { updateTagGlobally, bulkUpdateObjectTags, setFilters, setFilter, clearFilters, getObjects } from '../../store/Objects';
import { getPipelineBoard, setNewDealModal, setAdvanceFilter, toggleDeleteDeals, getDealsTable, getPipelineById, setPipelineSettingsModal, setCreatePipelineModal } from '../../store/Deals';

const Deals = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentWorkspace } = useSelector(state => state['Settings']);
  const dealsObjectData = useSelector(state => state['Objects']?.['deals']);
  const { deals, isDeleteDeals, pagination, advanceFilter, pipelineBoard, createPipelineModal, dealsTable } = useSelector(state => state['Deals']);

  const activeFilters = dealsObjectData?.['filters'] || { filterGroups: [], groupOperator: 'OR' };
  const allFilters = activeFilters?.['filterGroups']?.flatMap(g => (g['filters'] || []).map(f => ({ ...f, groupId: g['id'] }))) || [];

  const [selectItems, setSelectItems] = useState([]);
  const [activeView, setActiveView] = useState(null);
  const [isTableView, setTableView] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isSideViewModal, setIsSideViewModal] = useState(false);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [exportWizardOpen, setExportWizardOpen] = useState(false);

  const [currentFilters, setCurrentFilters] = useState({});

  const [anchorEl, setAnchorEl] = useState(null);
  const [tagsManagerOpen, setTagsManagerOpen] = useState(false);
  const handleCloseTagsManager = () => setAnchorEl(null);
  // jis button pe click hua usi ko anchor bana diya
  const handleOpenTagsManager = (event) => setAnchorEl(event.currentTarget);

  // Helper function to get total deals count from pipeline board
  const getTotalDealsFromPipeline = (board) => {
    if (!board?.['stages']) return 0
    let total = 0
    Object.values(board['stages']).forEach(stage => { total += (stage?.['deals'] || []).length })
    return total
  }

  useEffect(() => {
    dispatch(getProperties());
  }, []);

  // Get view type from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location['search']);
    const view = params.get('view');
    if (view === 'table') setTableView(true);
    else if (view === 'board') setTableView(false);
  }, [location['search']]);

  // Update URL when view changes
  const updateViewType = (isTable) => {
    setTableView(isTable);
    const params = new URLSearchParams(location['search']);
    params.set('view', isTable ? 'table' : 'board');
    history.push({ pathname: location.pathname, search: params.toString() });
  }

  const handleConfirmDelete = () => {
    dispatch(bulkDeleteObjects({
      objectType: 'deals',
      ids: selectItems,
      successCallback: () => {
        onGetData();
        setSelectItems([]);
        dispatch(toggleDeleteDeals(false));
      }
    }));
  }

  const toggleFiltersModal = () => dispatch(setAdvanceFilter('deals'));

  const handleRemoveFilter = (groupId, filterId) => {
    const updatedGroups = activeFilters['filterGroups'].map(group =>
      group['id'] === groupId ? { ...group, filters: group['filters'].filter(f => f['id'] !== filterId) } : group
    ).filter(group => group['filters'].length > 0);
    const updatedFilters = { ...activeFilters, filterGroups: updatedGroups };
    dispatch(setFilters({ objectType: 'deals', filters: updatedFilters }));
    if (!updatedGroups.length) dispatch(setFilter({ objectType: 'deals', filter: {} }));
    const mongoQuery = buildMongoQuery(updatedFilters['filterGroups'], updatedFilters['groupOperator']);
    onGetData({ filters: mongoQuery, filterDefinition: updatedFilters });
  };

  const handleClearFilters = () => {
    dispatch(clearFilters({ objectType: 'deals' }));
    dispatch(setFilter({ objectType: 'deals', filter: {} }));
    onGetData({ filters: {}, filterDefinition: { filterGroups: [], groupOperator: 'OR' } });
  };


  // Helper to get all deals from pipeline view
  const getAllDealsFromPipeline = () => {
    const allDeals = [];
    Object.values(pipelineBoard?.['stages'] || {}).forEach(stage => {
      const stageDeals = stage?.['deals'] || [];
      allDeals.push(...stageDeals);
    });
    return allDeals;
  };

  const onGetData = (data = {}) => {
    // Update current filters if provided
    if (data.filters !== undefined) setCurrentFilters(data.filters || {});

    const payload = {
      ...data,
      filters: data.filters !== undefined ? data.filters : currentFilters
    };

    if (isTableView) dispatch(getDealsTable(payload));
    else dispatch(getPipelineBoard(payload));
  }

  // ✅ Body class handle karne ka effect
  useEffect(() => {
    if (importWizardOpen || exportWizardOpen) document.body.classList.add("workflow-open");
    else document.body.classList.remove("workflow-open");

    // Cleanup: jab component unmount hoga tab class remove ho jaye
    return () => {
      document.body.classList.remove("workflow-open");
    };
  }, [importWizardOpen, exportWizardOpen]);

  return (
    <div className="content deal-page-sec">
      {/* --------------- Import Wizard Modal --------------- */}
      {importWizardOpen ?
        < ImportWizard
          objectType="deals"
          onComplete={onGetData}
          importWizardOpen={importWizardOpen}
          setImportWizardOpen={setImportWizardOpen}
          currentPipelineId={pipelineBoard?.['_id']}
        />
        : exportWizardOpen ?
          <ExportWizard
            objectType="deals"
            selectedIds={selectItems}
            exportWizardOpen={exportWizardOpen}
            setExportWizardOpen={setExportWizardOpen}
          />
          :
          <>
            <div className='main-container'>
              <div className='bottom-content-area'>
                <div className='tabs-content'>
                  <div className='content'>
                    <div className={`allemails-area campaign-page deals-page  ${isTableView ? 'tableview' : ''}`}>
                      <div className={`page-title-area `}>
                        <div>
                          <TitleRoute />
                          {pagination?.['total'] > 0 && <p className='records-title'>{pagination?.['total']} records</p>}
                        </div>
                        <div className='right-btn-area'>
                          {isTableView && (
                            <ViewManager
                              objectType="deals"
                              onViewChange={(view) => setActiveView(view)}
                              activeViewId={activeView?.['_id']}
                            />
                          )}
                          <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                            <DropdownToggle caret>
                              Actions
                              <svg width="4" height="4" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.99316 2.46804L1.91042 0.544434L0.307735 0.544434C0.0334783 0.544434 -0.10365 0.986291 0.090615 1.24531L1.57046 3.21843C1.80758 3.53459 2.19325 3.53459 2.43037 3.21843L2.99316 2.46804Z" fill="var(--primary-color)" />
                                <path d="M3.69092 0.544434L1.91111 0.544434L2.99386 2.46804L3.9109 1.24531C4.10231 0.986291 3.96518 0.544434 3.69092 0.544434Z" fill="var(--primary-color)" />
                              </svg>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem index={0}>
                                <div className='action-item' onClick={() => history.push(getWorkspacePath('/settings?tab=crm-properties', currentWorkspace?._id))}>
                                  <i className='icon'>
                                    <svg aria-label="Link opens in a new window" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="ExternalLink" class="StyledIcon-sc-14n04qn-0 cMjEKA"><path d="M26.91 5.78c-.07-.16-.16-.3-.28-.41a1.1 1.1 0 0 0-.4-.27c-.14-.06-.3-.09-.47-.09h-7.72c-.7 0-1.26.56-1.27 1.26 0 .7.57 1.27 1.27 1.27h4.64L11.86 18.35a1.272 1.272 0 0 0 1.8 1.8L24.47 9.34v4.64c0 .7.57 1.27 1.27 1.27s1.27-.57 1.27-1.27V6.27c0-.17-.03-.33-.09-.47Zm-4.57 11.91c-.7 0-1.27.57-1.27 1.27v5.15c0 .19-.16.35-.35.35H7.89c-.19 0-.35-.16-.35-.35V11.27c0-.19.16-.35.35-.35h5.15c.7 0 1.27-.57 1.27-1.27s-.57-1.27-1.27-1.27H7.89c-1.59 0-2.88 1.3-2.89 2.89v12.84a2.9 2.9 0 0 0 2.89 2.9h12.86c1.6 0 2.9-1.3 2.9-2.9v-5.14c0-.7-.57-1.27-1.27-1.27h-.03Z"></path></svg>
                                  </i>
                                  Edit properties
                                </div>
                              </DropdownItem>
                              <DropdownItem index={1}>
                                <div className='action-item' onClick={() => {
                                  if (pipelineBoard?.['_id']) {
                                    dispatch(getPipelineById(pipelineBoard['_id']));
                                    dispatch(setPipelineSettingsModal(true));
                                  }
                                }}>
                                  <i className='icon'>
                                    <svg aria-label="This feature is locked" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="Locked" class="StyledIcon-sc-14n04qn-0 cMjEKA"><path d="M25.68 11.7h-1.07v-1.07c0-4.76-3.86-8.62-8.62-8.62s-8.62 3.85-8.62 8.61v1.07H6.32c-2.08 0-3.77 1.69-3.77 3.77v10.76c0 2.08 1.69 3.77 3.77 3.77h19.37c2.08 0 3.77-1.69 3.77-3.77V15.46c0-2.08-1.69-3.77-3.77-3.77Zm-6.46 7.56c0 1.19-.96 2.15-2.15 2.15v3.22a1.071 1.071 0 0 1-2.14 0v-3.24c-1.19 0-2.16-.96-2.16-2.16s.96-2.16 2.16-2.16h2.15c1.19.02 2.14.98 2.15 2.17Zm-8.61-8.63c0-2.97 2.41-5.39 5.39-5.39s5.39 2.41 5.39 5.39v1.07H10.62v-1.07Z"></path></svg>
                                  </i>
                                  Edit pipeline
                                </div>
                              </DropdownItem>
                              <DropdownItem index={2}>
                                <div className='action-item' onClick={() => dispatch(setCreatePipelineModal(true))}>
                                  <i className='icon'>
                                    <svg aria-label="This feature is locked" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" focusable="false" data-icon-name="Locked" class="StyledIcon-sc-14n04qn-0 cMjEKA"><path d="M25.68 11.7h-1.07v-1.07c0-4.76-3.86-8.62-8.62-8.62s-8.62 3.85-8.62 8.61v1.07H6.32c-2.08 0-3.77 1.69-3.77 3.77v10.76c0 2.08 1.69 3.77 3.77 3.77h19.37c2.08 0 3.77-1.69 3.77-3.77V15.46c0-2.08-1.69-3.77-3.77-3.77Zm-6.46 7.56c0 1.19-.96 2.15-2.15 2.15v3.22a1.071 1.071 0 0 1-2.14 0v-3.24c-1.19 0-2.16-.96-2.16-2.16s.96-2.16 2.16-2.16h2.15c1.19.02 2.14.98 2.15 2.17Zm-8.61-8.63c0-2.97 2.41-5.39 5.39-5.39s5.39 2.41 5.39 5.39v1.07H10.62v-1.07Z"></path></svg>
                                  </i>
                                  Add another pipeline
                                </div>
                              </DropdownItem>
                              <DropdownItem index={3}>
                                <div className='action-item' onClick={() => setImportWizardOpen(true)}>
                                  <i className='icon fa fa-upload'></i> Import Deals
                                </div>
                              </DropdownItem>
                              <DropdownItem index={4}>
                                <div className='action-item' onClick={() => setExportWizardOpen(true)}>
                                  <i className='icon fa fa-download'></i> Export Deals
                                </div>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                          <button className='btn-style-new' onClick={() => dispatch(setNewDealModal(true))}>+ Add Deal</button>
                        </div>
                      </div>

                      <div className={`tables-deals-area `}>
                        <div className={`tables-deals-header style-deals`}>
                          <div className='filter-deals-owner'>
                            <>
                              <QuickFilters
                                objectType="deals"
                                onGetData={onGetData}
                                config={dealsQuickFilterConfig}
                                onAdvancedClick={toggleFiltersModal}
                              />
                              <div className='filter-table-select style-deals'>
                                <button className={`fiters-button ${!isTableView ? 'active' : ''}`} onClick={() => updateViewType(false)}>
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.82449 13.2707H2.46783C1.29533 13.2707 0.729492 12.7282 0.729492 11.6082V2.3915C0.729492 1.2715 1.30116 0.729004 2.46783 0.729004H4.82449C5.99699 0.729004 6.56283 1.2715 6.56283 2.3915V11.6082C6.56283 12.7282 5.99116 13.2707 4.82449 13.2707ZM2.46783 1.604C1.72699 1.604 1.60449 1.80234 1.60449 2.3915V11.6082C1.60449 12.1973 1.72699 12.3957 2.46783 12.3957H4.82449C5.56533 12.3957 5.68783 12.1973 5.68783 11.6082V2.3915C5.68783 1.80234 5.56533 1.604 4.82449 1.604H2.46783V1.604Z" fill="currentColor" />
                                    <path d="M11.5325 9.18734H9.17583C8.00333 9.18734 7.4375 8.64484 7.4375 7.52484V2.3915C7.4375 1.2715 8.00917 0.729004 9.17583 0.729004H11.5325C12.705 0.729004 13.2708 1.2715 13.2708 2.3915V7.52484C13.2708 8.64484 12.6992 9.18734 11.5325 9.18734ZM9.17583 1.604C8.435 1.604 8.3125 1.80234 8.3125 2.3915V7.52484C8.3125 8.114 8.435 8.31234 9.17583 8.31234H11.5325C12.2733 8.31234 12.3958 8.114 12.3958 7.52484V2.3915C12.3958 1.80234 12.2733 1.604 11.5325 1.604H9.17583V1.604Z" fill="currentColor" />
                                  </svg>
                                </button>
                                <button className={`fiters-button ${isTableView ? 'active' : ''}`} onClick={() => updateViewType(true)}>
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11.6087 13.2708H2.39199C1.27199 13.2708 0.729492 12.6992 0.729492 11.5325V9.17583C0.729492 8.00333 1.27199 7.4375 2.39199 7.4375H11.6087C12.7287 7.4375 13.2712 8.00917 13.2712 9.17583V11.5325C13.2712 12.6992 12.7287 13.2708 11.6087 13.2708ZM2.39199 8.3125C1.80283 8.3125 1.60449 8.435 1.60449 9.17583V11.5325C1.60449 12.2733 1.80283 12.3958 2.39199 12.3958H11.6087C12.1978 12.3958 12.3962 12.2733 12.3962 11.5325V9.17583C12.3962 8.435 12.1978 8.3125 11.6087 8.3125H2.39199Z" fill="currentColor" />
                                    <path d="M11.6087 6.56234H2.39199C1.27199 6.56234 0.729492 5.99067 0.729492 4.824V2.46734C0.729492 1.29484 1.27199 0.729004 2.39199 0.729004H11.6087C12.7287 0.729004 13.2712 1.30067 13.2712 2.46734V4.824C13.2712 5.99067 12.7287 6.56234 11.6087 6.56234ZM2.39199 1.604C1.80283 1.604 1.60449 1.7265 1.60449 2.46734V4.824C1.60449 5.56484 1.80283 5.68734 2.39199 5.68734H11.6087C12.1978 5.68734 12.3962 5.56484 12.3962 4.824V2.46734C12.3962 1.7265 12.1978 1.604 11.6087 1.604H2.39199V1.604Z" fill="currentColor" />
                                  </svg>
                                </button>
                              </div>
                            </>
                          </div>
                          {/* <SelectTag /> */}
                          {/* Bulk Actions Bar */}
                          <BulkActionsBar
                            objectType="deals"
                            onRefresh={onGetData}
                            selectedItems={selectItems}
                            onClearSelection={() => setSelectItems([])}
                            onDelete={() => dispatch(toggleDeleteDeals(true))}
                            totalItems={!isTableView ? getTotalDealsFromPipeline(pipelineBoard) : null}
                            customActions={selectItems.length > 0 ? [
                              {
                                label: 'Add tags',
                                icon: (
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.0292 9.15828C4.4417 9.15828 3.85004 8.93328 3.4042 8.48745L1.5167 6.59995C1.05837 6.14162 0.816703 5.50828 0.84587 4.86245L0.94587 2.77912C0.991703 1.78328 1.7792 0.995784 2.7792 0.945784L4.86254 0.845784C5.50837 0.820784 6.1417 1.05828 6.60004 1.51662L8.48754 3.40412C9.38337 4.29995 9.38337 5.76245 8.48754 6.65828L6.65837 8.48745C6.20837 8.93328 5.62087 9.15828 5.0292 9.15828ZM1.95837 6.15412L3.84587 8.04162C4.16254 8.35828 4.58337 8.53328 5.0292 8.53328C5.47504 8.53328 5.89587 8.35828 6.21254 8.04162L8.0417 6.21245C8.35837 5.89578 8.53337 5.47495 8.53337 5.02912C8.53337 4.58328 8.35837 4.16245 8.0417 3.84578L6.1542 1.95828C5.82087 1.62495 5.35837 1.44578 4.8917 1.47078L2.80837 1.57078C2.1292 1.59995 1.60004 2.12912 1.5667 2.80412L1.4667 4.88745C1.44587 5.35828 1.62504 5.82078 1.95837 6.15412Z" fill="var(--primary-color)" />
                                    <path d="M3.95829 5.31258C3.21246 5.31258 2.60413 4.70425 2.60413 3.95841C2.60413 3.21258 3.21246 2.60425 3.95829 2.60425C4.70413 2.60425 5.31246 3.21258 5.31246 3.95841C5.31246 4.70425 4.70413 5.31258 3.95829 5.31258ZM3.95829 3.22925C3.55829 3.22925 3.22913 3.55841 3.22913 3.95841C3.22913 4.35841 3.55829 4.68758 3.95829 4.68758C4.35829 4.68758 4.68746 4.35841 4.68746 3.95841C4.68746 3.55841 4.35829 3.22925 3.95829 3.22925Z" fill="var(--primary-color)" />
                                  </svg>
                                ),
                                onClick: handleOpenTagsManager
                              }
                            ] : []}
                            onSelectAll={!isTableView ? () => {
                              // For pipeline view, select all deals from all stages
                              const allDealIds = []
                              Object.values(pipelineBoard?.['stages'] || {}).forEach(stage => {
                                const stageDeals = stage?.['deals'] || []
                                stageDeals.forEach(deal => allDealIds.push(deal['_id']))
                              })
                              setSelectItems(allDealIds)
                            } : null}
                          />
                          <Menu anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleCloseTagsManager}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          >
                            <TagsManager
                              objectType="deals"
                              mode="select"
                              selectedObjects={selectItems}
                              onClose={handleCloseTagsManager}
                              onTagsApplied={() => {
                                setTagsManagerOpen(false);
                                setSelectItems([]);
                                // Refresh deals data
                                onGetData();
                              }}
                            />
                          </Menu>

                        </div>
                        {allFilters.length > 0 && (
                          <div className="active-filters-bar" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', padding: '8px 12px 4px' }}>
                            {allFilters.map(filter => {
                              let displayValue = filter['value'];
                              if (Array.isArray(displayValue)) displayValue = displayValue.join(', ');
                              else if (typeof displayValue === 'object' && displayValue !== null) {
                                const trueKeys = Object.keys(displayValue).filter(key => displayValue[key]);
                                displayValue = displayValue['label'] || displayValue['name'] || (trueKeys.length > 0 ? trueKeys.join(', ') : JSON.stringify(displayValue));
                              }
                              return (
                                <Chip
                                  size="small"
                                  key={filter['id']}
                                  label={`${filter['property']?.['label'] || filter['property']?.['name']} ${filter['operator']?.['label'] || ''} ${displayValue || ''}`}
                                  onDelete={() => handleRemoveFilter(filter['groupId'], filter['id'])}
                                  sx={{ background: 'var(--secondary-color)', color: 'var(--primary-color)', border: 'none', fontSize: '12px', '& .MuiChip-deleteIcon': { color: '#E94033' } }}
                                />
                              );
                            })}
                            <Button size="small" onClick={handleClearFilters}>Clear All</Button>
                          </div>
                        )}
                        <div className='bottom-detail-area'>
                          <div className='col-12'>
                            <div className='bottom-content-area-tasks'>
                              {isTableView
                                ? <TableView deals={deals} selectItems={selectItems} setSelectItems={setSelectItems} setIsSideViewModal={setIsSideViewModal} activeView={activeView} />
                                : <PipelineView deals={deals} selectItems={selectItems} setSelectItems={setSelectItems} setIsSideViewModal={setIsSideViewModal} />
                              }
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Activities />
            {isSideViewModal && <QuickView setIsSideViewModal={setIsSideViewModal} dealData={isSideViewModal} />}
            <PipelineSettings />
            {createPipelineModal && <PipelineSettings isCreateMode={true} onClose={() => dispatch(setCreatePipelineModal(false))} />}

            {/* --------------- Filters right Side Moodal--------------- */}
            <AdvancedFilterModal
              objectType="deals"
              onGetData={onGetData}
              isOpen={advanceFilter === 'deals'}
              onClose={() => dispatch(setAdvanceFilter(''))}
            />

            {/* --------------- Delete Deal Modal--------------- */}
            <Modal isOpen={isDeleteDeals} className={`main-modal modal-dialog-centered new-user-modal bulkedit-modal`}>
              <ModalHeader toggle={() => dispatch(toggleDeleteDeals(false))}></ModalHeader>
              <ModalBody>
                <div className='content-area'>
                  <div className='delete-modal'>
                    <h4>
                      <i className='icon'>
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path d='M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z' fill='#FF3B30' fill-opacity='0.15' />
                          <path d='M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5Z' fill='#FF3B30' />
                          <path d='M12 9V12' stroke='#FFE2E0' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                          <path d='M12 15H12.0083' stroke='#FFE2E0' strokeWidth='2' stroke-linecap='round' stroke-linejoin='round' />
                        </svg>
                      </i>
                      Delete Deal{selectItems.length > 1 && 's'}
                    </h4>
                    <p>Are you sure you want to delete <b>{selectItems.length} Deal{selectItems.length > 1 && 's'}</b>? You can't undo this action afterwards.</p>
                    <div className='form-area'>
                      <div className='btns-box text-right'>
                        <button className='btn-style-border' onClick={() => dispatch(toggleDeleteDeals(false))}>Cancel</button>
                        <button className='btn-style-new' onClick={handleConfirmDelete}> Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              </ModalBody>
            </Modal>

            {/* Tags Manager Modal */}
            {/* {tagsManagerOpen && (
              <Dialog
                open={tagsManagerOpen}
                onClose={() => setTagsManagerOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: '8px',
                    minWidth: '260px',
                    maxWidth: '320px',
                    minHeight: '400px'
                  }
                }}
              >
                <TagsManager
                  objectType="deals"
                  mode="select"
                  selectedObjects={selectItems}
                  onClose={() => setTagsManagerOpen(false)}
                  onTagsApplied={() => {
                    setTagsManagerOpen(false);
                    setSelectItems([]);
                    // Refresh deals data
                    onGetData();
                  }}
                />
              </Dialog>
            )} */}
          </>
      }
    </div >
  )
}

export default Deals